import React, { FC, useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { nextStep, setUserDetails } from "../../action/SpinToWinAction";
import { HKLogo, HKStar } from "../Images";
import classes from "./CMSPage.module.css";
import { RESET_ALL, RootState } from "../../reducers/RootReducer";
import { setInitialOption, setRingOptions, setSelectedRingDetails } from "../../action/RingAction";
import { setDiamondDetailsSuccess, setInitialDiamondOption, } from "../../action/DiamondAction";
import { connect, ConnectedProps, useDispatch, useSelector } from "react-redux";
import {
  setHeaderTabDefault,
  setHeaderTabToSetting,
  setHeaderTabToStud,
  setInitialHeader
} from "../../action/HeaderAction";
import { setFilter, setInitialFilter } from "../../action/FilterAction";
import { setExpandedRow, setTableInitialState } from "../../action/TableAction";
import { setInitialFooter } from "../../action/FooterAction";
import { setInitialStartWithSettingData } from "../../action/StartWithSettingFilterAction";
import { SetInitialTooltip } from "../../action/TooltipAction";
import UseCMSPage from "./useCMSPage";
import { PropsFromScreenManager } from "../../types";
import { setInitialCart } from "../../action/CartAction";
import StickyFooter from "../StickyFooter";
import Loader from "../Loader/Loader";
import DialogBox from "../DialogBox";
import Button from "../Button/Button";
import SpinIntro from "../SpinIntro";
import HowItWorks from "../HowItWorks";
import GetStarted, { UserDetails } from "../GetStarted";
import SpinTarget from "../SpinTarget";

const SPIN_BG = '/images/spin-bg.png';

// Steps 1-3 only. Step 0 is the attract screen and is meant to sit indefinitely.
const IDLE_STEPS = [ 1, 2, 3 ];
const IDLE_MS = 1000 * 30;            // no interaction before we ask if they're there
const IDLE_REDIRECT_SECONDS = 30; // countdown on the prompt before returning home
const IDLE_EVENTS: (keyof WindowEventMap)[] = [ 'pointerdown', 'keydown', 'touchstart', 'wheel' ];

// Everything the step transitions write inline styles to. Cleared whenever the
// journey lands back on step 0 so the next run starts from the stylesheet.
const GSAP_RESET_TARGETS = [
  '.spin-intro',
  '.gsap-primary-btn-next',
  '.gsap-hw-root', '.gsap-hw-wheel',
  '.gsap-gs-root', '.gsap-gs-wheel', '.gsap-gs-fade', '.gsap-gs-card',
  '.gsap-st-root', '.gsap-st-wheel', '.gsap-st-fade',
].join(', ');

const mapStateToProps = (state: RootState) => ({
  filter: state.filter,
  table:  state.table,
  ring:   state.ring,
});

const mapDispatchToProps = {
  setInitialOption,
  setHeaderTabDefault,
  setHeaderTabToSetting,
  setHeaderTabToStud,
  setInitialDiamondOption,
  setInitialFilter,
  setTableInitialState,
  setInitialFooter,
  setInitialHeader,
  setInitialStartWithSettingData,
  SetInitialTooltip,
  setDiamondDetailsSuccess,
  setExpandedRow,
  setRingOptions,
  setSelectedRingDetails,
  setInitialCart,
  setFilter,
};
const connector = connect(mapStateToProps, mapDispatchToProps);
export type PropsFromRedux = ConnectedProps<typeof connector>;
const CMSPage: FC<PropsFromRedux & PropsFromScreenManager> = (props) => {
  const { ContentArray } = UseCMSPage(props);
  const dispatch = useDispatch();
  const { step, remainingSeconds, targetPrice } = useSelector((state: RootState) => state.spinToWin);
  const navigatedRef = useRef(false);
  const spinBgRef = useRef<HTMLDivElement>(null);
  // Which step is handing off, or null when idle. Both pages stay mounted and
  // stacked while it runs so they can crossfade and the wheel can travel.
  const [ handoffFrom, setHandoffFrom ] = useState<number | null>(null);
  // Keeps Spin Intro on screen while How It Works fades in, so step 0 can leave
  // gradually instead of being cut the instant the step advances.
  const [ beginning, setBeginning ] = useState(false);
  const [ bgLoaded, setBgLoaded ] = useState(false);
  // Idle guard for the pre-challenge steps, so an abandoned kiosk returns to the
  // intro instead of holding someone else's half-finished journey.
  const [ isIdlePrompt, setIsIdlePrompt ] = useState(false);
  const [ idleCountdown, setIdleCountdown ] = useState(IDLE_REDIRECT_SECONDS);
  const pendingDetailsRef = useRef<UserDetails | null>(null);

  // On mount: reset all state except stateManager (which holds the current route)
  useEffect(() => {
    dispatch({ type: RESET_ALL });
  }, []);

  // Returning to step 0 mid-journey (idle timeout, or a spin that was skipped)
  // leaves every transition's inline styles behind: Spin Intro faded to 0, the
  // background zoomed to 300%, a half-travelled wheel. Wipe them all so the
  // intro opens clean instead of on the wreckage of the last run.
  useEffect(() => {
    if (step !== 0) return;

    // Kill anything still in flight first, or a live tween re-applies what we clear.
    gsap.killTweensOf(GSAP_RESET_TARGETS);
    gsap.set(GSAP_RESET_TARGETS, { clearProps: 'all' });
    if (spinBgRef.current) {
      gsap.killTweensOf(spinBgRef.current);
      gsap.set(spinBgRef.current, { clearProps: 'backgroundSize' });
    }

    // Overlay flags belong to a transition that no longer exists
    setBeginning(false);
    setHandoffFrom(null);
  }, [ step ]);

  // Watch for inactivity on the pre-challenge steps. Any input restarts the
  // clock; once the prompt is up it stops re-arming so a stray event can't
  // silently cancel the countdown — only the button does.
  useEffect(() => {
    if (!IDLE_STEPS.includes(step) || isIdlePrompt) return;

    let timer = 0;
    const restart = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setIsIdlePrompt(true), IDLE_MS);
    };

    IDLE_EVENTS.forEach(event => window.addEventListener(event, restart, { passive: true }));
    restart();

    return () => {
      window.clearTimeout(timer);
      IDLE_EVENTS.forEach(event => window.removeEventListener(event, restart));
    };
  }, [ step, isIdlePrompt ]);

  // Countdown on the prompt. The updater only computes the next number —
  // dispatching from inside it would fire twice under StrictMode, which
  // double-invokes updaters to surface impure ones.
  useEffect(() => {
    if (!isIdlePrompt) return;

    setIdleCountdown(IDLE_REDIRECT_SECONDS);
    const ticker = window.setInterval(() => {
      setIdleCountdown(remaining => Math.max(0, remaining - 1));
    }, 1000);

    return () => window.clearInterval(ticker);
  }, [ isIdlePrompt ]);

  // Reaching zero returns to the intro. RESET_ALL puts step back to 0, which
  // renders Spin Intro — no navigation needed, it's already this screen's step 0.
  useEffect(() => {
    if (!isIdlePrompt || idleCountdown > 0) return;

    setIsIdlePrompt(false);
    dispatch({ type: RESET_ALL });
  }, [ isIdlePrompt, idleCountdown ]);

  // A CSS background-image fires no load event, so probe it with an Image to
  // know when the backdrop is actually painted. Errors resolve too — a missing
  // file shouldn't leave the player stuck behind a spinner.
  useEffect(() => {
    const img = new Image();
    img.onload = () => setBgLoaded(true);
    img.onerror = () => setBgLoaded(true);
    img.src = SPIN_BG;
    // Already cached (the <head> preload usually wins this race)
    if (img.complete) setBgLoaded(true);
  }, []);

  // Circus-curtain zoom on step 0 → 1, fade out content on exit
  /*useEffect(() => {
    const el = spinBgRef.current;
    if (!el) return;

    if (prevStepRef.current === 0 && step === 1) {
      // Start from cover (no zoom), animate to 300%
      gsap.fromTo(
        el,
        { backgroundSize: '100%' },
        { backgroundSize: '300%', duration: 2, ease: 'power3.out' }
      );
    } else if (step !== 0) {
      gsap.set(el, { backgroundSize: '300%' });
    } else {
      gsap.set(el, { backgroundSize: '100%' });
    }

    prevStepRef.current = step;
  }, [step]);*/

  // Starts background zoom on click; animates HowItWorks content when zoom completes
  const onBeginTransition = () => {
    const el = spinBgRef.current;
    if (!el) return;
    // const targets = document.querySelectorAll('.hw-header');
    /* gsap.fromTo(targets, { y: 30, scale: 1 }, {
       y:        0,
       scale:    0.8,
       duration: 0.5,
       ease:     'circ',
       delay:    0.3
     });*/
    // Step 0 leaves and step 1 arrives over the same window, so neither is abrupt
    // and the screen never goes empty in between.
    setBeginning(true);
    gsap.to('.spin-intro', { opacity: 0, duration: 0.4, ease: 'power2.inOut' });
    gsap.set('.gsap-hw-wheel', { clearProps: 'opacity' });
    gsap.fromTo('.gsap-hw-root', { opacity: 0 }, {
      opacity:  1,
      duration: 0.9,
      ease:     'power2.inOut',
      // Deliberately leaves Spin Intro at opacity 0. Clearing it here would restore
      // it to full opacity synchronously while it's still display:block, flashing
      // it back over How It Works; the effect below resets it on return to step 0.
      onComplete: () => setBeginning(false),
    });
    const targetPrimaryNextButton = document.querySelector(
      '.gsap-primary-btn-next'
    );

    const nextButton = targetPrimaryNextButton as HTMLElement;
    gsap.fromTo(
      nextButton,
      { y: -100 },
      {
        y:        0,
        duration: 0.7,
        ease:     'circ.out',
        delay:    0.1,
      }
    );

    gsap.fromTo(
      el,
      { backgroundSize: '100%' },
      {
        backgroundSize: '300%', duration: 1, ease: 'power3.inOut',
      }
    );
  };

  // Step 1 → 2 (How It Works → Get Started)
  const onNextTransition = () => setHandoffFrom(1);

  // Step 2 → 3 (Get Started → Spin Target). Details ride along so they can be
  // committed with the step change once the wheel finishes growing.
  const onSubmitTransition = (details: UserDetails) => {
    pendingDetailsRef.current = details;
    setHandoffFrom(2);
  };

  useEffect(() => {
    if (handoffFrom === null) return;

    // Outgoing page + its wheel, incoming page + its wheel and the bits that fade in
    const prefixes = handoffFrom === 1
      ? { out: 'hw', in: 'gs' }
      : { out: 'gs', in: 'st' };

    const outRoot = document.querySelector(`.gsap-${prefixes.out}-root`) as HTMLElement | null;
    const outWheel = document.querySelector(`.gsap-${prefixes.out}-wheel`) as HTMLElement | null;
    const inRoot = document.querySelector(`.gsap-${prefixes.in}-root`) as HTMLElement | null;
    const inWheel = document.querySelector(`.gsap-${prefixes.in}-wheel`) as HTMLElement | null;
    const inFade = Array.from(document.querySelectorAll(`.gsap-${prefixes.in}-fade`)) as HTMLElement[];

    const commit = () => {
      if (handoffFrom === 2 && pendingDetailsRef.current) {
        // setUserDetails also advances the step
        dispatch(setUserDetails(pendingDetailsRef.current));
        pendingDetailsRef.current = null;
      } else {
        dispatch(nextStep());
      }
      setHandoffFrom(null);
    };

    if (!outRoot || !outWheel || !inRoot || !inWheel) {
      commit();
      return;
    }

    // FLIP: where the wheel sits now vs. where it needs to land
    const from = outWheel.getBoundingClientRect();
    const to = inWheel.getBoundingClientRect();
    const dx = from.left - to.left;
    const dy = from.top - to.top;
    const scale = from.width / to.width;

    // The incoming wheel starts hidden and crossfades in over the outgoing one.
    // A hard swap pops here: Get Started's card overlaps its wheel, so only the
    // top half shows, while the incoming wheel sits on a higher overlay and is
    // unclipped — swapping instantly makes the hidden half snap into view.
    gsap.set(inRoot, { opacity: 1 });
    gsap.set(inFade, { opacity: 0 });
    gsap.set(inWheel, { x: dx, y: dy, scale, transformOrigin: 'top left', opacity: 0 });

    const tl = gsap.timeline({
      onComplete: () => {
        // Only reset the incoming page. Clearing the outgoing page's opacity here
        // would make it flash at full opacity, since it stays display:block until
        // React commits the step change below.
        gsap.set([ inRoot, inWheel, ...inFade ], { clearProps: 'all' });
        commit();
      },
    });

    tl.to(outRoot, { opacity: 0, duration: 0.7, ease: 'power2.inOut' }, 0);
    tl.to(inFade, { opacity: 1, duration: 0.7, ease: 'power2.inOut' }, 0.15);
    // Ease the form card down to half so the growing wheel reads through it. Tweened
    // rather than set, or it snaps from 1 to 0.5 in a single frame. The page fade
    // above then multiplies it the rest of the way to 0.
    if (handoffFrom === 2) {
      tl.to('.gsap-gs-card', { opacity: 0.5, duration: 0.45, ease: 'power2.out' }, 0);
    }
    // Hand the wheel over gradually so the clipped → unclipped change isn't a pop
    tl.to(outWheel, { opacity: 0, duration: 0.4, ease: 'power2.out' }, 0);
    tl.to(inWheel, { opacity: 1, duration: 0.4, ease: 'power2.out' }, 0);
    tl.to(inWheel, { x: 0, y: 0, scale: 1, duration: 0.95, ease: 'power3.inOut' }, 0);

    // If the handoff is cancelled part-way — the idle timeout resetting to step 0
    // is the one that happens — the timeline must not survive to run commit() and
    // advance the step out from under the reset.
    return () => {
      tl.kill();
    };
  }, [ handoffFrom ]);

  // When challenge starts, navigate to the setting page
  useEffect(() => {
    const { screenManager } = props.instanceData as any;
    if (!screenManager?.changeScreen) return;

    if (step >= 4 && !navigatedRef.current) {
      if (!targetPrice) {
        // No target price means spin was skipped — send back to home
        dispatch({ type: RESET_ALL });
        return;
      }
      navigatedRef.current = true;
      screenManager.changeScreen({
        viewName:     'setting',
        styleId:      null,
        searchParams: { command: 'build', rpid: '000000' },
      });
    }

    // Restart journey — step reset to 0 after having navigated away
    if (step === 0 && navigatedRef.current) {
      navigatedRef.current = false;
      screenManager.changeScreen({ viewName: 'home', styleId: null });
    }
  }, [ step ]);

  const isSpinFlow = step >= 0 && step <= 3;

  if (step === 0 && !bgLoaded) {
    return (
      <Loader position="absolute"/>
    )
  }

  if (isSpinFlow) {
    return (
      <div
        ref={spinBgRef}
        className={classes.spinBg}
        style={{ backgroundImage: `url('${SPIN_BG}')` }}
      >
        <div
          className={beginning ? classes.stepOverlay : undefined}
          style={{ display: (step === 0 && bgLoaded) || beginning ? 'block' : 'none' }}
        >
          <SpinIntro onBeginTransition={onBeginTransition}/>
        </div>
        <div
          className={`size-full${beginning || handoffFrom === 1 ? ` ${classes.stepOverlay}` : ''}`}
          style={{ display: step === 1 || beginning || handoffFrom === 1 ? 'block' : 'none' }}
        >
          <HowItWorks isActive={step === 1} onNextTransition={onNextTransition}/>
        </div>
        <div
          className={handoffFrom !== null ? classes.stepOverlay : undefined}
          style={{ display: step === 2 || handoffFrom !== null ? 'block' : 'none' }}
        >
          <GetStarted isActive={step === 2} onSubmitTransition={onSubmitTransition}/>
        </div>
        <div
          className={handoffFrom === 2 ? classes.stepOverlay : undefined}
          style={{ display: step === 3 || handoffFrom === 2 ? 'block' : 'none' }}
        >
          <SpinTarget isActive={step === 3}/>
        </div>

        {/* Idle prompt. closeModal is wired to the same dismiss as the button so
            a backdrop tap counts as "still here" — any deliberate input should. */}
        <DialogBox
          isOpen={isIdlePrompt}
          closeModal={() => setIsIdlePrompt(false)}
          title=""
          isIcon={false}
          containerClassName={classes.idleDialogContainer}
          panelClassName={classes.idleDialogPanel}
        >
          <div className={classes.idleCard}>
            <p className={classes.idleLabel}>ARE YOU STILL THERE?</p>
            <p className={classes.idleCountdown}>{idleCountdown}</p>
            <p className={classes.idleDesc}>
              We'll start over the game in{' '}
              <strong>{idleCountdown} second{idleCountdown === 1 ? '' : 's'}</strong>.
            </p>
            <Button
              type="button"
              className={classes.idleBtn}
              onClick={() => setIsIdlePrompt(false)}
            >
              I'm still here
            </Button>
          </div>
        </DialogBox>
      </div>
    );
  }

  /* useEffect(() => {
        if (screenManager) {
            (screenManager as any).props.setStateManagerData(
                'currentScreen',
                'CMS Screen',
            );
            (screenManager as any).props.setStateManagerData(
                'currentView',
                'Main View',
            );
        }
    }, []);*/
  /*const handleResetState = (key: string,path:string) => {
        if (key === 'Start With a Diamond') {
            setHeaderTabDefault();
        } else if (key === 'Start With a Setting' || key === 'Design Your Own') {
            setHeaderTabToSetting(key);
        }
        setInitialOption();
        setInitialDiamondOption();
        setInitialFilter();
        setTableInitialState();
        if (
            screenManager &&
            (screenManager as unknown as ScreenManagerABC).changeScreen
        ) {
            (screenManager as unknown as ScreenManagerABC).changeScreen({
                viewName: path,
                id: null,
            });
        }
    };*/
  return (
    <>
      <main className={classes.root}>
        <section className={classes.section}>
          <div
            className={`${classes.w_layout_blockContainer} ${classes.container} ${classes.w_container}`}
          >
            <div className={classes.logo}>
              <img
                src={HKLogo}
                loading="lazy"
                width={320}
                alt={"Customise Ring"}
                className={classes.logo_img}
              />
            </div>
            <div className={classes.ring_builder}>
              <h1 className={classes.title}>CUSTOMIZE YOUR ENGAGEMENT RING</h1>
              <h2 className={classes.subtitle}>Your Ring, Your Way</h2>
            </div>
            <div className={classes.bar}>
              <img
                src={HKStar}
                loading="lazy"
                width={160}
                alt={"Setting"}
                className={classes.barImg}
              />
            </div>
            <div className={classes._3section}>
              {ContentArray.map((content, index) => {
                return (
                  <div
                    tabIndex={0}
                    className={`${classes._3section_wrapper}`}
                    key={index}
                    onClick={() => {
                      content.onClick(content.linkTO, content.styleId);
                    }}
                    onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                      if (event.code === "Enter" || event.code === "Space") {
                        content.onClick(content.linkTO, content.styleId);
                      }
                    }}
                  >
                    <div className={classes._3section_img_wrapper}>
                      <img
                        src={content.imagePath}
                        loading="lazy"
                        alt={content.imageAlt}
                      />
                    </div>
                    <div className={classes._3section_text_wrapper}>
                      <h2 className={classes._3section_title}>
                        {content.heading}
                      </h2>
                      <p className={classes._3section_para}>
                        {content.paragraph}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </main>
      <StickyFooter instanceData={props.instanceData}/>
    </>
  );
};
export default connector(CMSPage);
